/**
 * Stories & guides — the API contract, shared by the server (routes →
 * services → repositories) and the client (hooks, pages, and the demo-mode
 * mock, which answers the identical shapes). Every response is the standard
 * envelope `{ ok: true, data: T }` or `{ ok: false, error: { code, … } }`.
 *
 *   GET    /api/stories?category=&cursor=&saved=1   → StoryListResponse
 *   GET    /api/stories/submissions                 → StorySubmission[]        (mine)
 *   GET    /api/stories/submissions/:id             → StorySubmissionDetail    (mine)
 *   PUT    /api/stories/submissions/:id             → StorySubmission          (mine, while submitted)
 *   DELETE /api/stories/submissions/:id             → { deleted: true }        (mine, any status)
 *   GET    /api/stories/:slug                       → StoryDetail              (records a read)
 *   POST   /api/stories                             → SubmitStoryResponse      (201)
 *   POST   /api/stories/:slug/thanks                → ThankStoryResponse
 *   POST   /api/stories/:slug/save                  → SaveStoryResponse
 *   POST   /api/stories/:slug/helpful               → HelpfulStoryResponse
 *
 *   GET    /api/admin/stories/submissions?status=   → AdminStorySubmission[]   (platform admin)
 *   POST   /api/admin/stories/submissions/:id/publish  → AdminStorySubmission
 *   POST   /api/admin/stories/submissions/:id/reject   → AdminStorySubmission
 *
 * Two kinds of item share one list: **guides** (team-authored, reviewed
 * content kept in code, shared/content/storiesGuides.ts) and **stories**
 * (members' submissions an admin has published). Market rules are enforced
 * on the server: PH-only content never reaches a main-market account.
 */

/** The topics a story can live under. Family stories come first, on purpose. */
export const STORY_CATEGORIES = [
  "Family stories",
  "New parents",
  "Toddlers",
  "School",
  "Health",
  "Typhoon & rainy season",
  "Working parents",
  "Mental health",
  "Dads",
] as const;

export type StoryCategory = (typeof STORY_CATEGORIES)[number];

/**
 * Renamed topics, old name → new. "Birth stories" became "Family stories" in
 * October 2026: migration 0025 renamed the stored rows (its CHECK still allows
 * the old name, so nothing written mid-deploy is rejected), and an old
 * bookmark, a shared link or a tab opened before the deploy may still send
 * the old name. The list filter, the write schemas and every row read accept
 * it and hand on the new one, so nobody ever sees the old name again.
 */
const RENAMED_STORY_CATEGORIES: ReadonlyMap<string, StoryCategory> = new Map<string, StoryCategory>([
  ["Birth stories", "Family stories"],
]);

/** The current name for a topic: an old name maps to its new one; anything else comes back as is. */
export function currentStoryCategory(value: string): string {
  return RENAMED_STORY_CATEGORIES.get(value) ?? value;
}

/** `category` plus every old name that now means it — for matching stored rows. */
export function storyCategoryNames(category: StoryCategory): string[] {
  const old = [...RENAMED_STORY_CATEGORIES].filter(([, now]) => now === category).map(([name]) => name);
  return [category, ...old];
}

/**
 * Topics only the Philippines market sees. The server filters them out of
 * every list, detail and write for a main-market account; the migration's
 * CHECK also pins a row in one of them to `market = 'ph'`.
 */
export const PH_ONLY_STORY_CATEGORIES: readonly StoryCategory[] = ["Typhoon & rainy season"];

/**
 * Topics that always carry "Shared experience, not medical advice — ask your
 * pediatrician." A member's submission in one of them gets the note automatically.
 */
export const HEALTH_NOTE_CATEGORIES: readonly StoryCategory[] = [
  "Family stories",
  "New parents",
  "Health",
  "Mental health",
];

/**
 * Covers are keys into a fixed illustration set — never uploads — so no photo
 * of a child can ever be attached to a story. Members pick from the eight clay
 * scenes; the two drawn covers are for team guides.
 */
export const MEMBER_COVER_IDS = [
  "hero-family",
  "list-mom",
  "circle-family",
  "cta-family",
  "moment-date",
  "moment-daycare",
  "moment-grandparents",
  "moment-practice",
] as const;

export const EDITOR_COVER_IDS = ["rain-rainbow", "night-sky"] as const;

export type MemberCoverId = (typeof MEMBER_COVER_IDS)[number];
export type StoryCoverId = MemberCoverId | (typeof EDITOR_COVER_IDS)[number];
export const STORY_COVER_IDS: readonly StoryCoverId[] = [...MEMBER_COVER_IDS, ...EDITOR_COVER_IDS];

/**
 * In-article blocks a guide can place on a line of its own as `{{name}}`.
 * Member text never carries them: the server strips those lines on write.
 */
export const STORY_EMBEDS = ["care-link", "support", "doh-4s", "signals"] as const;
export type StoryEmbed = (typeof STORY_EMBEDS)[number];

/**
 * The community badges an author can show. Mirrors the badge ids the
 * community feature uses (`_sim/people.ts` BadgeId); kept as a literal union
 * here so this contract has no client import.
 */
export type StoryBadgeId =
  | "mentorParent"
  | "verifiedNurse"
  | "verifiedDoctor"
  | "verifiedTeacher"
  | "foundingMember"
  | "storySharer"
  | "kindNeighbor"
  | "preparedParent";

/** "guide" = team-authored, reviewed content in code; "story" = a member's published story. */
export type StoryKind = "guide" | "story";

/** The author id every team guide carries. */
export const TEAM_AUTHOR_ID = "leenas-list-team";

export interface StoryPerson {
  id: string;
  /** The author's name as shown; never an email and never a child's name. */
  name: string;
  /** How the community knows them, e.g. "Ate Joy". */
  displayName: string;
  /** e.g. "Mom of three". May be empty. */
  role: string;
  /** May be empty. */
  city: string;
  avatarUrl: string | null;
  badges: StoryBadgeId[];
}

export interface StoryAuthor extends StoryPerson {
  /** One or two sentences for the author card at the end of a story. May be empty. */
  bio: string;
}

/** The official body a guide cites, e.g. the Department of Health. */
export interface StorySource {
  name: string;
  url: string | null;
}

/**
 * Who checked a health guide. A guide awaiting a clinician's read says so
 * ("Reviewed by: pending clinician review") — a status, never an invented name.
 */
export type StoryClinicalReview = { status: "pending" } | { status: "reviewed"; by: string };

/** The signed-in reader's own relationship to a story. */
export interface StoryViewerState {
  read: boolean;
  saved: boolean;
  thanked: boolean;
  /** null until the reader answers "Was this helpful?". */
  helpful: boolean | null;
}

export interface StorySummary {
  slug: string;
  kind: StoryKind;
  title: string;
  /** One-line summary for cards and the article header. */
  dek: string;
  category: StoryCategory;
  /** Finer topics, used to pick related stories. */
  tags: string[];
  coverId: StoryCoverId;
  author: StoryAuthor;
  /** Set on guides that cite an official source (DOH, PAGASA). */
  source: StorySource | null;
  /** Set on health guides; see StoryClinicalReview. */
  clinicalReview: StoryClinicalReview | null;
  /** Health-related: the UI shows the "not medical advice" line. */
  healthNote: boolean;
  readingMinutes: number;
  /** "Helped N parents" — readers who answered yes to "Was this helpful?". */
  helpedCount: number;
  thanksCount: number;
  publishedAt: string;
  /** Staging sample content: the UI shows a small "Sample" chip. */
  isSample: boolean;
  viewer: StoryViewerState;
}

export interface StoryListQuery {
  category?: StoryCategory;
  /** Opaque cursor from the previous page's nextCursor. */
  cursor?: string;
  /** Only the reader's saved stories (all categories). */
  saved?: boolean;
}

export interface StoryListResponse {
  /** The editors' pick — first page of the unfiltered list only, never repeated in items. */
  featured: StorySummary | null;
  /** Newest first, 8 per page. */
  items: StorySummary[];
  nextCursor: string | null;
}

export interface StoryDetail extends StorySummary {
  /**
   * Plain text with simple formatting: a blank line starts a new paragraph,
   * **bold**, "- " starts a bullet, "## " a heading, and (guides only) a
   * `{{embed}}` line places a StoryEmbed block. Rendered as React elements,
   * never as HTML.
   */
  body: string;
  /** Up to three other stories, same topic first. */
  related: StorySummary[];
}

/** Preset one-tap notes for "Thank the author" — gratitude without a comment thread. */
export const THANKS_NOTES = ["salamat", "notAlone", "sharing"] as const;
export type ThanksNoteId = (typeof THANKS_NOTES)[number];

export interface ThankStoryRequest {
  note?: ThanksNoteId | null;
}

/** Idempotent: one thank-you per reader per story (the first note wins). */
export interface ThankStoryResponse {
  thanked: true;
  thanksCount: number;
}

export interface SaveStoryRequest {
  saved: boolean;
}

export interface SaveStoryResponse {
  saved: boolean;
}

export interface HelpfulStoryRequest {
  helpful: boolean;
}

/** The reader may change their answer; helpedCount reflects the latest one. */
export interface HelpfulStoryResponse {
  helpful: boolean;
  helpedCount: number;
}

/**
 * The review state machine: `submitted → published | rejected`, plus one
 * safety valve, `published → rejected` (an admin can take a published story
 * down, with a note). `rejected` is final; nothing returns to `submitted`.
 */
export const STORY_SUBMISSION_STATUSES = ["submitted", "published", "rejected"] as const;
export type StorySubmissionStatus = (typeof STORY_SUBMISSION_STATUSES)[number];

export interface SubmitStoryRequest {
  title: string;
  category: StoryCategory;
  body: string;
  coverId: MemberCoverId;
  /** A line about the writer for the author card, e.g. "First-time mom in Cebu". */
  authorBio?: string;
}

/** One of the caller's own submissions, as listed on "Your stories". */
export interface StorySubmission {
  id: string;
  title: string;
  category: StoryCategory;
  coverId: MemberCoverId;
  status: StorySubmissionStatus;
  wordCount: number;
  submittedAt: string;
  reviewedAt: string | null;
  /** The reviewer's note to the writer (always set on a rejection). */
  reviewNote: string | null;
  /** Set once published: the story's public slug. */
  slug: string | null;
}

/** GET /api/stories/submissions/:id — the full text, for editing while in review. */
export interface StorySubmissionDetail extends StorySubmission {
  body: string;
  authorBio: string;
}

export interface SubmitStoryResponse {
  submission: StorySubmission;
}

/** The admin console's view of a submission: accounts, never child records. */
export interface AdminStorySubmission extends StorySubmission {
  body: string;
  authorBio: string;
  /** Staging sample content (the seeder's in-review family story): shown with a "Sample" chip. */
  isSample: boolean;
  author: {
    id: string;
    /** The name the story would be published under. */
    displayName: string;
    email: string;
  };
}

export interface ReviewStoryRequest {
  /** Optional on publish; required on reject. Shown to the writer. */
  note?: string;
}
